/** Word search grid helpers. */

export const DEFAULT_GRID_SIZE = 12;

export function createEmptyGrid(size) {
  return Array.from({ length: size }, () => Array(size).fill(""));
}

export function canPlaceWord(grid, word, row, col, direction, gridSize) {
  if (direction === 0) {
    if (col + word.length > gridSize) return false;
    for (let i = 0; i < word.length; i++) {
      if (grid[row][col + i] !== "") return false;
    }
  } else if (direction === 1) {
    if (row + word.length > gridSize) return false;
    for (let i = 0; i < word.length; i++) {
      if (grid[row + i][col] !== "") return false;
    }
  } else {
    if (row + word.length > gridSize || col + word.length > gridSize) return false;
    for (let i = 0; i < word.length; i++) {
      if (grid[row + i][col + i] !== "") return false;
    }
  }
  return true;
}

export function placeWord(grid, word, row, col, direction) {
  if (direction === 0) {
    for (let i = 0; i < word.length; i++) grid[row][col + i] = word[i];
  } else if (direction === 1) {
    for (let i = 0; i < word.length; i++) grid[row + i][col] = word[i];
  } else {
    for (let i = 0; i < word.length; i++) grid[row + i][col + i] = word[i];
  }
}

export function fillGridWithRandomLetters(grid) {
  const size = grid.length;
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (grid[row][col] === "") {
        grid[row][col] = String.fromCharCode(65 + Math.floor(Math.random() * 26));
      }
    }
  }
  return grid;
}

export function placeWordsInGrid(grid, words, gridSize) {
  const placed = [];
  words.forEach((word) => {
    let ok = false;
    let attempts = 0;
    while (!ok && attempts < 120) {
      const direction = Math.floor(Math.random() * 3);
      const row = Math.floor(Math.random() * gridSize);
      const col = Math.floor(Math.random() * gridSize);
      if (canPlaceWord(grid, word, row, col, direction, gridSize)) {
        placeWord(grid, word, row, col, direction);
        placed.push({ word, row, col, direction });
        ok = true;
      }
      attempts++;
    }
  });
  return placed;
}

export function buildWordSearchGrid(wordList, gridSize = DEFAULT_GRID_SIZE) {
  const words = wordList.map((w) => String(w).toUpperCase().replace(/[^A-Z]/g, ""));
  const grid = createEmptyGrid(gridSize);
  const placements = placeWordsInGrid(grid, words, gridSize);
  fillGridWithRandomLetters(grid);
  return { grid, placements, words };
}

/** Cells on a straight line (horizontal, vertical, or diagonal) between two coordinates. */
export function getCellsOnPath(start, end) {
  const dr = end.row - start.row;
  const dc = end.col - start.col;
  const absDr = Math.abs(dr);
  const absDc = Math.abs(dc);
  if (!(dr === 0 || dc === 0 || absDr === absDc)) return [];
  const steps = Math.max(absDr, absDc);
  const stepR = dr === 0 ? 0 : dr / absDr;
  const stepC = dc === 0 ? 0 : dc / absDc;
  const cells = [];
  for (let i = 0; i <= steps; i++) {
    cells.push({
      row: start.row + i * stepR,
      col: start.col + i * stepC,
    });
  }
  return cells;
}

export function cellKey(row, col) {
  return `${row},${col}`;
}

export function parseWordPuzzleFromTheme(themeData) {
  const wp = themeData?.wordpuzzle;
  let list = [];
  if (Array.isArray(wp)) {
    list = wp;
  } else if (wp && typeof wp === "object") {
    list = wp.words || [];
  }
  if (typeof wp === "string") {
    try {
      const parsed = JSON.parse(wp);
      list = parsed?.words || (Array.isArray(parsed) ? parsed : []);
    } catch {
      list = [];
    }
  }
  return list
    .map((item) => ({
      word: String(item?.word ?? item?.name ?? "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z]/g, ""),
      hint: String(item?.hint ?? item?.indication ?? "").trim(),
    }))
    .filter((e) => e.word);
}

export function parseHintDisplayMode(themeData) {
  const wp = themeData?.wordpuzzle;
  const raw =
    (typeof wp === "object" && wp !== null && !Array.isArray(wp)
      ? wp.hint_display_mode
      : null) ||
    themeData?.wordpuzzle_hint_display_mode ||
    themeData?.hint_display_mode ||
    "show_full_word";
  if (raw === "show_first_letter" || raw === "show_hint_only") return raw;
  return "show_full_word";
}

export function formatWordListLabel(entry, mode) {
  const w = entry.word;
  const hint = String(entry.hint ?? "").trim();

  if (mode === "show_hint_only") {
    return hint;
  }
  if (mode === "show_first_letter") {
    if (!hint) return "";
    return w.length ? `${w[0]}${" _".repeat(Math.max(0, w.length - 1))}` : w;
  }
  return w;
}
